// Marketing landing page. Wordmark click jumps straight into the app;
// the CTA routes through the community picker first.
import AdminFooter from "./AdminFooter.jsx";
import React from "react";

const FEATURES = [
  {
    title: "Counsel",
    body: "Ask any board governance question. Get precise answers grounded in California's Davis-Stirling Act and your governing documents.",
    icon: <path d="M12 2 3 6v6c0 5 4 9 9 10 5-1 9-5 9-10V6l-9-4z" />,
  },
  {
    title: "Association Laws",
    body: "Browse and query your CC&Rs and community documents. AI retrieves the exact sections relevant to your question.",
    icon: (
      <>
        <path d="M12 3v18" />
        <path d="M5 8l-3 6a4 4 0 0 0 6 0z" />
        <path d="M19 8l-3 6a4 4 0 0 0 6 0z" />
        <path d="M5 8h14" />
        <path d="M8 21h8" />
      </>
    ),
  },
  {
    title: "Simple Setup",
    body: "Upload your CC&Rs as a Markdown or text file. All data stays in your browser — nothing is sent to third-party servers.",
    icon: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
      </>
    ),
  },
];

export default function Landing({ onEnterApp, onEnterPicker }) {
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--ink)", display: "flex", flexDirection: "column" }}>
      <header style={{ borderBottom: "2px solid var(--ink)", padding: "22px 40px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 16, height: 16, background: "var(--accent)", flex: "none" }} />
          <span style={{ font: "800 18px/1 var(--bq-font-display)", letterSpacing: "-0.02em" }}>BoardIQ</span>
        </div>
        <nav style={{ display: "flex", alignItems: "center", gap: 28, font: "600 12px/1 var(--bq-font-display)", letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted2)" }}>
          <span>Counsel</span>
          <span>Association Laws</span>
          <span>Simple Setup</span>
        </nav>
      </header>

      <main style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "96px 24px 64px" }}>
        <button onClick={onEnterApp} style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }} aria-label="Enter BoardIQ">
          <h1 style={{ margin: 0, font: "800 96px/0.9 var(--bq-font-display)", letterSpacing: "-0.04em", color: "var(--ink)" }}>
            Board<span style={{ color: "var(--accent)" }}>IQ</span>
          </h1>
        </button>

        <div style={{ width: 64, height: 2, background: "var(--ink)", margin: "28px 0" }} />

        <p style={{ margin: 0, maxWidth: 640, font: "400 18px/1.6 var(--bq-font-body)", color: "var(--body)" }}>
          AI-powered governance assistant for HOA board members. Query California's
          Davis-Stirling Act, your CC&amp;Rs, and community documents with natural language.
        </p>

        <button
          onClick={onEnterPicker}
          style={{ marginTop: 40, display: "inline-flex", alignItems: "center", gap: 10, background: "var(--accent)", color: "var(--onAccent)", border: "2px solid var(--ink)", padding: "16px 32px", font: "700 13px/1 var(--bq-font-display)", letterSpacing: ".08em", textTransform: "uppercase", cursor: "pointer" }}
        >
          Enter the application
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14" /><path d="M13 6l6 6-6 6" />
          </svg>
        </button>

        <div style={{ width: "100%", maxWidth: 1040, marginTop: 88, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", borderTop: "2px solid var(--ink)", borderLeft: "2px solid var(--ink)" }}>
          {FEATURES.map((f) => (
            <div key={f.title} style={{ borderRight: "2px solid var(--ink)", borderBottom: "2px solid var(--ink)", padding: "32px 28px", textAlign: "left", display: "flex", flexDirection: "column", gap: 16, minHeight: 220 }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{f.icon}</svg>
              <div style={{ font: "800 17px/1.1 var(--bq-font-display)", letterSpacing: "-0.01em" }}>{f.title}</div>
              <div style={{ font: "400 13.5px/1.55 var(--bq-font-body)", color: "var(--body)" }}>{f.body}</div>
            </div>
          ))}
        </div>
      </main>

      <AdminFooter static showAdminLink />
    </div>
  );
}
